import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, TimerIcon, Send, Flag } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg, runJob } from "../lib/api";
import { MarkingResults } from "../components/MarkingResults";

const fmt = (secs) => {
  const m = Math.floor(Math.abs(secs) / 60);
  const s = Math.abs(secs) % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export default function ExamMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [paper, setPaper] = useState(null);
  const [answers, setAnswers] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const submittedRef = useRef(false);
  const answersRef = useRef({});
  const elapsedRef = useRef(0);
  const storageKey = `eg_exam_${id}`;

  useEffect(() => {
    api.get(`/papers/${id}`).then((r) => {
      setPaper(r.data);
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
        if (saved?.answers) { setAnswers(saved.answers); answersRef.current = saved.answers; }
        if (saved?.elapsed) { setElapsed(saved.elapsed); elapsedRef.current = saved.elapsed; }
      } catch (e) { /* ignore */ }
    }).catch(() => navigate("/papers"));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!paper || result || submitting) return;
    const t = setInterval(() => {
      setElapsed((e) => {
        const next = e + 1;
        elapsedRef.current = next;
        if (next % 10 === 0) localStorage.setItem(storageKey, JSON.stringify({ answers: answersRef.current, elapsed: next }));
        if (paper.time_allowed && next >= paper.time_allowed * 60 && !submittedRef.current) {
          submittedRef.current = true;
          toast.info("Time's up — submitting your answers for marking");
          setTimeout(() => submit(true), 100);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [paper, result, submitting]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAnswer = (n, v) => {
    const next = { ...answersRef.current, [n]: v };
    answersRef.current = next;
    setAnswers(next);
  };

  const submit = async (auto = false) => {
    const current = answersRef.current;
    if (!auto && !Object.values(current).some((a) => a?.trim())) {
      return toast.error("Write at least one answer before submitting");
    }
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const payload = {
        paper_id: id,
        time_taken_minutes: Math.max(1, Math.round(elapsedRef.current / 60)),
        answers: Object.entries(current).map(([n, a]) => ({ number: parseInt(n), answer: a || "" })).filter((a) => a.answer.trim()),
      };
      if (!payload.answers.length) payload.answers = [{ number: 1, answer: "(no answer given)" }];
      const job = await runJob(() => api.post("/examiner/mark", payload));
      const { data } = await api.get(`/examiner/submissions/${job.result_id}`);
      setResult(data);
      localStorage.removeItem(storageKey);
      toast.success("Marking complete!");
    } catch (e) {
      submittedRef.current = false;
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!paper) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const timed = paper.time_allowed > 0;
  const remaining = timed ? paper.time_allowed * 60 - elapsed : elapsed;
  const low = timed && remaining <= 300;
  const answered = Object.values(answers).filter((a) => a?.trim()).length;

  if (result) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="exam-results-page">
        <Link to="/analytics" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="exam-to-progress-link">
          <ArrowLeft className="h-4 w-4" /> View in My Progress
        </Link>
        <h1 className="text-3xl font-bold tracking-tighter mb-6">Results — {paper.title}</h1>
        <MarkingResults result={result} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="exam-mode-page">
      <div className="sticky top-16 z-40 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 glass bg-background/85 border-b border-border flex items-center justify-between gap-4" data-testid="exam-timer-bar">
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{paper.title}</p>
          <p className="text-xs text-muted-foreground">{answered}/{paper.questions.length} answered • {paper.total_marks} marks</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`flex items-center gap-2 rounded-full border px-4 py-1.5 font-display font-bold tabular-nums ${low ? "border-rose-500/50 text-rose-400 animate-pulse" : "border-border"}`} data-testid="exam-timer">
            <TimerIcon className="h-4 w-4" />{fmt(remaining)}{!timed && <span className="text-[10px] font-normal text-muted-foreground">elapsed</span>}
          </span>
          <Button size="sm" className="rounded-full glow-btn" data-testid="exam-submit-btn" disabled={submitting} onClick={() => submit(false)}>
            {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Marking…</> : <><Send className="h-4 w-4 mr-1.5" />Finish & Mark</>}
          </Button>
        </div>
      </div>

      {submitting ? (
        <div className="rounded-3xl border border-border bg-card p-12 mt-8 text-center" data-testid="exam-marking-loading">
          <Loader2 className="h-7 w-7 animate-spin text-primary mx-auto mb-4" />
          <p className="font-medium">A senior examiner is marking your paper…</p>
          <p className="text-sm text-muted-foreground mt-2">Marking against real AQA level descriptors — usually 1–2 minutes.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          <div className="rounded-2xl bg-secondary p-4 text-xs text-muted-foreground flex items-center gap-2">
            <Flag className="h-3.5 w-3.5 text-primary shrink-0" />
            {timed ? `You have ${paper.time_allowed} minutes. Answers auto-save and will be submitted automatically when time runs out.` : "Untimed practice — take as long as you need. Answers auto-save as you type."}
          </div>
          {paper.questions.map((q) => (
            <div key={q.number} className="rounded-3xl border border-border bg-card p-6" data-testid={`exam-question-${q.number}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold font-display">Question {q.number}</span>
                <div className="flex gap-2">
                  <Badge variant="outline" className="text-xs">{q.command_word}</Badge>
                  <Badge className="bg-primary/15 text-primary hover:bg-primary/15 text-xs">{q.marks} marks</Badge>
                </div>
              </div>
              {q.figure_description && <p className="text-xs italic text-muted-foreground mb-2 rounded-xl bg-secondary p-3">Figure {q.number}: {q.figure_description}</p>}
              <p className="text-sm leading-relaxed whitespace-pre-line mb-4">{q.text}</p>
              <Textarea data-testid={`exam-answer-${q.number}`} rows={q.marks >= 8 ? 10 : q.marks >= 4 ? 5 : 3}
                placeholder="Type your answer…" value={answers[q.number] || ""} onChange={(e) => setAnswer(q.number, e.target.value)} />
            </div>
          ))}
          <Button className="w-full rounded-full glow-btn h-11" data-testid="exam-submit-bottom-btn" disabled={submitting} onClick={() => submit(false)}>
            <Send className="h-4 w-4 mr-2" />Finish & Mark My Paper
          </Button>
        </div>
      )}
    </div>
  );
}
